import { WebRTCErrorType } from '../types/webrtc';

export interface WebRTCErrorMessage {
  title: string;
  description: string;
  troubleshooting?: string;
}

export const WEBRTC_ERROR_MESSAGES: Record<WebRTCErrorType | 'PERMISSION_DENIED' | 'HTTPS_REQUIRED', WebRTCErrorMessage> = {
  [WebRTCErrorType.CONNECTION_FAILED]: {
    title: 'Connection Failed',
    description: 'Could not establish a voice connection. This might be due to a poor network or a restrictive firewall.',
    troubleshooting: 'Try switching from Wi-Fi to mobile data, or check if you are behind a corporate VPN/firewall.',
  },
  [WebRTCErrorType.SIGNALING_ERROR]: {
    title: 'Signaling Error',
    description: 'The voice service is temporarily unavailable.',
    troubleshooting: 'Please try again in a few moments.',
  },
  [WebRTCErrorType.ICE_FAILED]: {
    title: 'Network Blocked',
    description: 'Your network environment is blocking the voice stream (NAT traversal failed).',
    troubleshooting: 'Corporate networks often block voice traffic. Try using mobile data.',
  },
  [WebRTCErrorType.DATACHANNEL_ERROR]: {
    title: 'Data Channel Error',
    description: 'The messaging channel for voice chat was interrupted.',
    troubleshooting: 'A refresh might help, or the system will automatically fall back to text mode.',
  },
  'PERMISSION_DENIED': {
    title: 'Microphone Blocked',
    description: 'Access to your microphone was denied.',
    troubleshooting: 'Click the camera/lock icon in your address bar to allow microphone access.',
  },
  'HTTPS_REQUIRED': {
    title: 'Secure Connection Required',
    description: 'Voice features require a secure (HTTPS) connection to access the microphone.',
    troubleshooting: 'Please ensure you are accessing the site via https://',
  }
};

/**
 * Get user-friendly error message based on error type and language
 */
export function getWebRTCErrorMessage(type: string, language: 'en' | 'ar'): WebRTCErrorMessage {
  const base = WEBRTC_ERROR_MESSAGES[type as keyof typeof WEBRTC_ERROR_MESSAGES] || WEBRTC_ERROR_MESSAGES[WebRTCErrorType.CONNECTION_FAILED];
  
  if (language === 'ar') {
    const arMessages: Record<string, WebRTCErrorMessage> = {
      [WebRTCErrorType.CONNECTION_FAILED]: {
        title: 'فشل الاتصال',
        description: 'لم نتمكن من إنشاء اتصال صوتي. قد يكون ذلك بسبب ضعف الشبكة أو جدار حماية تقييدي.',
        troubleshooting: 'جرّب التبديل من الواي فاي إلى بيانات الهاتف، أو تحقق مما إذا كنت تستخدم VPN.',
      },
      [WebRTCErrorType.SIGNALING_ERROR]: {
        title: 'خطأ في الخدمة',
        description: 'خدمة الصوت غير متوفرة مؤقتاً.',
        troubleshooting: 'يرجى المحاولة مرة أخرى بعد قليل.',
      },
      [WebRTCErrorType.ICE_FAILED]: {
        title: 'الشبكة محظورة',
        description: 'بيئة الشبكة الخاصة بك تمنع تدفق الصوت.',
        troubleshooting: 'غالباً ما تحظر شبكات الشركات حركة مرور الصوت. جرّب استخدام بيانات الهاتف.',
      },
      [WebRTCErrorType.DATACHANNEL_ERROR]: {
        title: 'خطأ في قناة البيانات',
        description: 'انقطع اتصال قناة الرسائل الصوتية.',
        troubleshooting: 'قد يساعد تحديث الصفحة، أو سيقوم النظام بالتحول تلقائياً إلى الوضع النصي.',
      },
      'PERMISSION_DENIED': {
        title: 'الميكروفون محظور',
        description: 'تم رفض الوصول إلى الميكروفون الخاص بك.',
        troubleshooting: 'انقر على أيقونة الكاميرا/القفل في شريط العناوين للسماح بالوصول إلى الميكروفون.',
      },
      'HTTPS_REQUIRED': {
        title: 'مطلوب اتصال آمن',
        description: 'تتطلب ميزات الصوت اتصالاً آمناً (HTTPS) للوصول إلى الميكروفون.',
        troubleshooting: 'يرجى التأكد من الدخول للموقع عبر https://',
      }
    };
    return arMessages[type] || arMessages[WebRTCErrorType.CONNECTION_FAILED];
  }

  return base;
}

